import { defineComponent as _defineComponent } from 'vue'
import { getJoinedDepartmentName } from '@/utils/string';
import { buildDepartmentPathMap } from '@/utils/departmentTree';
import { SortState } from '@/composables/useSds';
import { computed, PropType, ref } from 'vue';
import { Sds } from '@/interfaces/model/general/sds';
import { omitString } from '@/lib/formatter';
import { formatToDate } from '@/utils/datetime';
import { TagResponse } from '@/interfaces/model/general/tag';
import { Department } from '@/interfaces/model/general/department';
import TagChip from '@/components/TagChip.vue';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { splitTextWithLinks } from '@/utils/splitTextWithLinks';
import SdsTablePseudoScrollBar from '@/views/dashboard/SdsTablePseudoScrollBar.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'DynamicListResultTable',
  props: {
  safetyDataSheets: {
    type: Array as PropType<Array<Sds>>,
    default: () => [
      {
        id: 1,
        uuid: '5bcee672-8a7b-4c69-8929-7d7653adfa59',
        name: 'ethanol.pdf',
        note: 'メモ',
        admin_note: 'メモ',
        timestamp: 1560000000,
        status_id: 10000,
        company_id: '9b0186d2-26fc-4dc8-833d-14904d31610e',
        user_id: '09c94b5b-feb4-430f-b1e5-aa18c7587e17',
        published_at: '2022-10-03',
        revised_at: '2022-10-03',
        created_at: '2022-10-03T06:40:48.580289',
        updated_at: '2022-10-03T06:40:48.580289',
        deleted_at: null,
      },
    ],
  },
  total: {
    type: Number,
    default: 1,
  },
  allSdsCheck: {
    type: Boolean,
    default: false,
  },
  page: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 100,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  activeSds: {
    type: Object as PropType<Sds>,
    default: undefined,
  },
  sortState: {
    type: Object as PropType<SortState>,
    default: () => ({
      name: 'none',
      revised_at: 'none',
      created_at: 'none',
      updated_at: 'none',
    }),
  },
},
  emits: ["showSdsDetail", "onClickProductName", "onClickDepartment", "onClickTag", "toggleSort", "update:page", "update:itemsPerPage"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'showSdsDetail', item: Sds, index: number): void;
  (e: 'onClickProductName', name: string): void;
  (e: 'onClickDepartment', department: Department): void;
  (e: 'onClickTag', tag: TagResponse): void;
  (e: 'toggleSort', columnName: string): void;
  (e: 'update:page', page: number): void;
  (e: 'update:itemsPerPage', itemsPerPage: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);

const footerProps = computed(() => ({
  'items-per-page-options': [50, 100, 500],
  'items-per-page-text': $t(sharedMessages.itemsPerPageText),
}));





const { isOwnDepartment, companyDepartments: allCompanyDepartments } =
  useDepartmentComposable();

/**
 * 親が違えば同名の部署を作れる仕様なので、名前だけでは同名の部署を区別できない
 * (3 階層以上では直近の親も同名になりうるため、1 階層上の親名だけでも不十分)。
 * 親の見える範囲は role/所属で絞られる場合があるため、絞り込み前の一覧 (useDepartmentComposable 由来)
 * からフルパスを組む。
 */
const departmentPathByUuid = computed(() =>
  buildDepartmentPathMap(allCompanyDepartments.value)
);
const departmentLabel = (department: Department): string =>
  departmentPathByUuid.value.get(department.uuid) ?? department.name;

const updatePage = (e: number) => {
  emits('update:page', e);
};

const updateItemsPerPage = (e: number) => {
  emits('update:itemsPerPage', e);
};

const headers = [
  {
    text: '',
    value: 'check',
    sortable: false,
    width: '30px',
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.pdfName),
    value: 'name',
    sortable: false,
    width: '200px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.productName),
    value: 'uuid',
    sortable: false,
    width: '280px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.note),
    value: 'note',
    sortable: false,
    width: '200px',
    maxWidth: '200px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.cause),
    value: 'cause',
    sortable: false,
    width: '200px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.sdsRevisedAt),
    value: 'revised_at',
    sortable: false,
    width: '140px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.pdfUploadDate),
    value: 'created_at',
    sortable: false,
    width: '160px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.checklistAddDate),
    value: 'updated_at',
    sortable: false,
    width: '180px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.assignee),
    value: 'assignee_users',
    sortable: false,
    width: '155px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.department),
    value: 'departments',
    sortable: false,
    width: '120px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
  {
    text: $t(sharedMessages.tag),
    value: 'tags',
    sortable: false,
    width: '60px',
    divider: true,
    class: 'grey lighten-3 font-weight-bold',
  },
];

const isActiveSds = (uuid: string): boolean => {
  return uuid === props.activeSds?.uuid;
};

const dataTable = ref(null);

return { __sfc: true,$t, footerProps, props, emits, isOwnDepartment, allCompanyDepartments, departmentPathByUuid, departmentLabel, updatePage, updateItemsPerPage, headers, isActiveSds, dataTable, getJoinedDepartmentName, omitString, formatToDate, TagChip, sharedMessages, splitTextWithLinks, SdsTablePseudoScrollBar }
}

})